import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const StarterPackage = () => {
  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  const features = [
    { name: "1 Social Platform Managed", included: true, description: "Focus on your primary platform - Instagram, Facebook, or LinkedIn" },
    { name: "8 Posts Per Month", included: true, description: "Consistent presence with 2 posts per week" },
    { name: "Basic Content Calendar", included: true, description: "Simple monthly planning for your content" },
    { name: "Monthly Engagement Report", included: true, description: "Track your growth and engagement metrics" },
    { name: "Canva Graphics (branded)", included: true, description: "Professional branded visuals for your posts" },
    { name: "Caption Copywriting", included: true, description: "Engaging captions that convert" },
    { name: "Paid Ad Management", included: false },
    { name: "Video Editing", included: false },
    { name: "SEO Support", included: false },
    { name: "Priority Support", included: false }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Header onContactClick={scrollToContact} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-brand-brown/20 to-black"></div>
        <div className="absolute top-20 right-20 w-96 h-96 bg-brand-yellow/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-brand-cream/70 hover:text-brand-yellow transition-colors duration-300 mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to All Packages
          </button>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-brand-yellow/20 rounded-full border border-brand-yellow/40 mb-6">
                <span className="text-brand-yellow font-bold">STARTER PACKAGE</span>
              </div>
              <h1 className="text-6xl lg:text-7xl font-bold text-white mb-6">
                <span className="text-brand-yellow">£99</span>/month
              </h1>
              <p className="text-2xl text-brand-cream/90 mb-8">
                Perfect for brand new businesses just starting their digital journey
              </p>
              <Button 
                onClick={scrollToContact}
                size="lg"
                className="bg-brand-yellow hover:bg-brand-yellow/90 text-brand-brown font-bold px-12 py-6 text-xl"
              >
                Get Started
              </Button>
            </div>
            
            <div className="bg-gradient-to-br from-brand-brown/60 to-black/80 rounded-3xl p-10 border-2 border-brand-olive/40 shadow-2xl backdrop-blur-xl">
              <h3 className="text-2xl font-bold text-white mb-6">What's Included</h3>
              <ul className="space-y-4">
                {features.slice(0, 6).map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-brand-yellow flex-shrink-0 mt-1" />
                    <div>
                      <span className="text-brand-cream/90 font-medium">{feature.name}</span>
                      {feature.description && (
                        <p className="text-brand-cream/60 text-sm mt-1">{feature.description}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Wave Transition */}
      <div className="relative">
        <svg className="w-full h-24" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="url(#gradient1)" />
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(165, 166, 117, 0.1)" />
              <stop offset="50%" stopColor="rgba(236, 203, 51, 0.1)" />
              <stop offset="100%" stopColor="rgba(165, 166, 117, 0.1)" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Detailed Features */}
      <section className="py-20 px-6 bg-gradient-to-b from-brand-brown/10 to-black">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-white mb-12 text-center">Perfect For</h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gradient-to-br from-brand-brown/40 to-black/60 rounded-2xl p-8 border border-brand-olive/30">
              <h3 className="text-xl font-bold text-brand-yellow mb-4">New Startups</h3>
              <p className="text-brand-cream/80">Just launched and need to establish your online presence without breaking the bank.</p>
            </div>
            <div className="bg-gradient-to-br from-brand-brown/40 to-black/60 rounded-2xl p-8 border border-brand-olive/30">
              <h3 className="text-xl font-bold text-brand-yellow mb-4">Solo Entrepreneurs</h3>
              <p className="text-brand-cream/80">Focus on your business while we handle your social media consistently.</p>
            </div>
            <div className="bg-gradient-to-br from-brand-brown/40 to-black/60 rounded-2xl p-8 border border-brand-olive/30">
              <h3 className="text-xl font-bold text-brand-yellow mb-4">Testing the Waters</h3>
              <p className="text-brand-cream/80">Want to try professional social media management before committing to larger packages.</p>
            </div>
          </div>

          {/* Navigation to other packages */}
          <div className="flex justify-between items-center mt-20">
            <div className="text-brand-cream/50">
              ← No previous package
            </div>
            <button
              onClick={() => navigate('/packages/basic')}
              className="flex items-center gap-2 text-brand-yellow hover:text-brand-yellow/80 transition-colors duration-300"
            >
              Next: Basic Package
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Wave Transition */}
      <div className="relative">
        <svg className="w-full h-24 rotate-180" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="url(#gradient2)" />
          <defs>
            <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(165, 166, 117, 0.1)" />
              <stop offset="50%" stopColor="rgba(236, 203, 51, 0.1)" />
              <stop offset="100%" stopColor="rgba(165, 166, 117, 0.1)" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <Footer />
    </div>
  );
};

export default StarterPackage;
